import React from 'react'
import type { ComponentConfig, Slot } from '@puckeditor/core'
import * as styles from './Grid.module.scss'
import { getClassNameFactory } from '../../utils/get-class-name-factory'
import { Section } from '../../components/Section'
import { withLayout } from '../../components/Layout'

const getClassName = getClassNameFactory('Grid', styles)

export type GridProps = {
  numColumns: number
  customColumns: boolean
  columnsTemplate: string
  gap: number
  alignItems: 'start' | 'center' | 'end' | 'stretch' | 'baseline'
  items: Slot
}

const CustomSlot = (props: any) => {
  return <span {...props} />
}

const allGridFields: ComponentConfig<GridProps>['fields'] = {
  customColumns: {
    type: 'radio',
    label: 'Custom Column Layout',
    options: [
      { label: 'No', value: false },
      { label: 'Yes', value: true },
    ],
  },
  numColumns: {
    type: 'number',
    label: 'Number of columns',
    min: 1,
    max: 12,
  },
  columnsTemplate: {
    type: 'text',
    label: 'grid-template-columns',
    placeholder: 'e.g. 1fr 2fr / 200px 1fr auto',
  },
  gap: {
    label: 'Gap',
    type: 'number',
    min: 0,
  },
  alignItems: {
    label: 'Align Items',
    type: 'select',
    options: [
      { label: 'Stretch', value: 'stretch' },
      { label: 'Start', value: 'start' },
      { label: 'Center', value: 'center' },
      { label: 'End', value: 'end' },
      { label: 'Baseline', value: 'baseline' },
    ],
  },
  items: {
    type: 'slot',
    disallow: ['Hero', 'Stats'],
  },
}

const isCustom = (v: unknown) => v === true || v === 'true'

export const GridInternal: ComponentConfig<GridProps> = {
  // What the editor's agent reads about this block (AgentComponentNotes).
  ai: {
    description:
      'Columns of child blocks (numColumns equal columns, or columnsTemplate). Stacks into one column under 768px.',
    fields: {
      columnsTemplate:
        'Only with customColumns true. CSS grid-template-columns, e.g. 2fr 1fr.',
      gap: 'Pixels between cells.',
    },
  },
  fields: allGridFields,
  defaultProps: {
    numColumns: 4,
    customColumns: false,
    columnsTemplate: '',
    gap: 24,
    alignItems: 'stretch',
    items: [],
  },
  resolveFields: data => {
    const custom = isCustom(data.props.customColumns)
    return {
      customColumns: allGridFields.customColumns,
      ...(custom
        ? { columnsTemplate: allGridFields.columnsTemplate }
        : { numColumns: allGridFields.numColumns }),
      gap: allGridFields.gap,
      alignItems: allGridFields.alignItems,
      items: allGridFields.items,
    } as NonNullable<ComponentConfig<GridProps>['fields']>
  },
  render: ({
    gap,
    numColumns,
    customColumns,
    columnsTemplate,
    alignItems,
    items: Items,
  }) => {
    const gridTemplateColumns =
      isCustom(customColumns) && columnsTemplate?.trim()
        ? columnsTemplate
        : `repeat(${numColumns}, 1fr)`
    return (
      <Section paddingInline="0">
        <Items
          as={CustomSlot}
          disallow={['Hero', 'Stats']}
          className={`${getClassName()} no-slot-padding`}
          style={{
            gap,
            alignItems,
            gridTemplateColumns,
          }}
        />
      </Section>
    )
  },
}

export const Grid = withLayout(GridInternal)
